<template>
  <form v-if="isLoaded" @submit.prevent="submitForm" novalidate qualification-id="" submit-form user-id=""
        working-day-id="">

    <div class="row">
      <div class="col-lg-3 col-md-6 col-sm-12">
        <div v-if="yearsDropdown && yearsDropdown.length">
          <SelectVue
              v-model="formData.year"
              :options="yearsDropdown"
              :label="$t('Year')"
              @update:modelValue="onChangeYearAndMonth"
              :required="true"
              :validation="v$.year"
              :messages="v$.year.required.$message"/>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 col-sm-12">
        <div v-if="monthsDropdown && monthsDropdown.length">
          <SelectVue
              v-model="formData.month"
              :options="monthsDropdown"
              :label="$t('Month')"
              @update:modelValue="onChangeYearAndMonth"
              :required="true"
              :validation="v$.month"
              :messages="v$.month.required.$message"/>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 col-sm-12">
        <BaseNumber
            v-model="formData.planedHoursForMonth"
            :label="$t('Arbeitszeit im Monat')"
            :required="true"
            :validation="v$.planedHoursForMonth"
            :messages="v$.planedHoursForMonth.required.$message"/>
      </div>
      <div class="col-lg-3 col-md-6 col-sm-12">
        <BaseNumber
            v-model="formData.planedMinutesForMonth"
            :label="$t('Arbeitsminuten im Monat')"
            :required="true"
            :validation="v$.planedMinutesForMonth"
            :messages="v$.planedMinutesForMonth.required.$message"/>
      </div>
    </div>

    <div class="row" style="border-top: 1px solid #D8D8D8">
      <div class="col-12">
        <label class="mt-3 mb-3">{{ $t('Arbeitstage') }}</label>
        <div v-if="v$.workingDays.$error" class="text-danger">
          <label>Bitte wählen Sie Werktage aus</label>
        </div>
        <div class="row">
          <div v-for="day in daysInMonthDropdown"
               :key="day.value"
               class="col-3 d-flex mb-2">
            <label class="custom-checkbox position-relative">
              <input type="checkbox"
                     :id="day.value"
                     :value="day.value"
                     v-model="formData.workingDays"
                     class="custom-checkbox position-absolute"/>
              <div class="custom-check-icon"></div>
            </label>
            <label :for="'day-' + day.value" class="form-check-label">{{ day.label }}</label>
          </div>
        </div>
      </div>
    </div>

<!--    <div class="row">-->
<!--      <div class="col-12">-->
<!--        <SelectVue-->
<!--            v-model="formData.workingDays"-->
<!--            :options="daysInMonthDropdown"-->
<!--            :placeholder="$t('Arbeitstage')"-->
<!--            :validation="v$.workingDays"-->
<!--            :required="true"-->
<!--            :is-multi="true"-->
<!--            :is-clear="true"-->
<!--            :messages="v$.workingDays.required.$message"/>-->
<!--      </div>-->
<!--    </div>-->

    <div class="d-flex justify-content-center w-100 mt-3">
      <SaveButton
          :label="$t('general.button.save')"
          :loading="isSubmitting"/>
    </div>
  </form>
</template>

<script setup>
import userService from '@/api/User';
import BaseNumber from '@/components/base/BaseNumber.vue';
import SelectVue from '@/components/SelectVue.vue';
import { ErrorToast, SuccessToast } from '@/components/VueToast';
import { requiredRule } from '@/shared/validators/validationRules';
import useVuelidate from '@vuelidate/core';
import { computed, defineEmits, defineProps, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import SaveButton from '@/components/button/SaveButton.vue';

const { t }               = useI18n();
const isLoaded            = ref(false);
const isSubmitting        = ref(false);
const daysInMonthDropdown = ref([]);
const emit                = defineEmits([ 'update:modelValue', 'update:submitForm' ]);

const props = defineProps({
  userId: {
    type: [ String, Number ],
    required: true,
  },
  yearsDropdown: {
    type: Array,
    require: true,
  },
  monthsDropdown: {
    type: Array,
    require: true,
  },
  workingDayId: {
    type: [ String, Number ],
    required: true,
  },
  submitForm: {
    type: Boolean,
    required: true,
    default: false,
  },
});

const currentDate = new Date();

const formData = ref({
  userId: '',
  year: currentDate.getFullYear(),  // Current year as a string
  month: currentDate.getMonth() + 1,
  planedHoursForMonth: '',
  planedMinutesForMonth: '',
  workingDays: [],
});

const rules = computed(() => {
  return {
    year: requiredRule(t),
    month: requiredRule(t),
    planedHoursForMonth: requiredRule(t),
    planedMinutesForMonth: requiredRule(t),
    workingDays: requiredRule(t),
  };
});

const v$ = useVuelidate(rules, formData);

async function submitForm() {

  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {

    if (props.workingDayId) {
      await userService.putStudentWorkingDays(+props.userId, +props.workingDayId, formData.value);
    } else {
      await userService.postStudentWorkingDays(+props.userId, formData.value);
    }

    emit('update:modelValue', false);
    emit('update:submitForm', !props.submitForm);

    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getWorkingDaysData(id) {
  try {
    const { data } = await userService.getSingleStudentWorkingDay(+id);
    await getWorkingDaysDropdown(data.data.year, data.data.month);
    formData.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function onChangeYearAndMonth() {
  const { data }             = await userService.getDaysInMonth(+formData.value.year, +formData.value.month);
  formData.value.workingDays = [];
  daysInMonthDropdown.value  = [];
  daysInMonthDropdown.value  = data.data;
}

async function getWorkingDaysDropdown(year, month) {
  const { data }            = await userService.getDaysInMonth(+year, +month);
  daysInMonthDropdown.value = [];
  daysInMonthDropdown.value = data.data;
}

onMounted(async () => {
  if (props.workingDayId) {
    await getWorkingDaysData(props.workingDayId);
  } else {
    await onChangeYearAndMonth(formData.value.year, formData.value.month);
  }

  isLoaded.value = true;
});
</script>
<style scoped>
.custom-checkbox .custom-check-icon {
  position: relative;
  display: inline-block;
  width: 18px;
  min-width: 18px;
  height: 18px;
  min-height: 18px;
  border: 2px solid #4caf50;
  border-radius: 4px;
  background-color: white;
  margin-right: 8px;
}

.custom-checkbox .custom-check-icon::before {
  content: '';
  position: absolute;
  top: 1px;
  left: 4px;
  width: 6px;
  height: 11px;
  border: solid #4caf50;
  border-width: 0 3px 3px 0;
  transform: rotate(45deg);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.custom-checkbox input[type="checkbox"]:checked + .custom-check-icon::before {
  opacity: 1;
}

.custom-checkbox .custom-check-icon:hover {
  border-color: #389142;
}
</style>